import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ReactComponent as HorseHeadIcon } from '../assets/icons/horse-head.svg';


const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];


const EVENT_COLORS = {
  lesson: '#2E8B68',
  show: '#D33E72',
  farrier: '#5547B5',
  vet: '#2164A8',
  bodywork: '#176D5A',
  worming: '#BA7517',
  vaccine: '#BA7517',
  dental: '#6C4AA1',
  other: '#77746F'
};


const EVENT_TINTS = {
  lesson: '#F1F8F4',
  show: '#FDF1F5',
  farrier: '#F4F2FB',
  vet: '#F2F6FC',
  bodywork: '#EFF7F4',
  worming: '#FFF7EA',
  vaccine: '#FFF7EA',
  dental: '#F5F1F9',
  other: '#F6F5F3'
};


const EVENT_LABELS = {
  lesson: 'Lesson',
  show: 'Show',
  farrier: 'Farrier',
  vet: 'Vet',
  bodywork: 'Bodywork',
  worming: 'Worming',
  vaccine: 'Vaccine',
  dental: 'Dental',
  other: 'Other'
};


const SvgIcon = ({ name, size = 20 }) => {
  const base = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    width: size,
    height: size,
    'aria-hidden': true
  };

  switch (name) {
    case 'calendar':
      return (
        <svg {...base}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M8 3v4M16 3v4M3 10h18" />
          <path d="M8 14h.01M12 14h.01M16 14h.01" />
        </svg>
      );

    case 'vet':
      return (
        <svg {...base}>
          <path d="M12 4v16M4 12h16" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      );

    case 'farrier':
      return (
        <svg {...base}>
          <path d="M6 4v7a6 6 0 0 0 12 0V4" />
          <path d="M9 4v7a3 3 0 0 0 6 0V4" />
          <circle cx="7.5" cy="7" r=".6" fill="currentColor" stroke="none" />
          <circle cx="16.5" cy="7" r=".6" fill="currentColor" stroke="none" />
          <circle cx="8" cy="11" r=".6" fill="currentColor" stroke="none" />
          <circle cx="16" cy="11" r=".6" fill="currentColor" stroke="none" />
        </svg>
      );

    case 'show':
      return (
        <svg {...base}>
          <circle cx="12" cy="9" r="5" />
          <circle cx="12" cy="9" r="2.5" />
          <path d="M9 13 7 22l5-3 5 3-2-9" />
          <path d="m9.5 5.2.8 1.2 1.7-.3 1.2 1.2-.3 1.7 1.2.8-1.2.8.3 1.7-1.2 1.2-1.7-.3-.8 1.2-.8-1.2-1.7.3-1.2-1.2.3-1.7-1.2-.8 1.2-.8-.3-1.7 1.2-1.2 1.7.3Z" />
        </svg>
      );

    case 'bodywork':
      return (
        <svg {...base}>
          <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 10c0 5.5-7 10-7 10Z" />
        </svg>
      );

    case 'worming':
    case 'vaccine':
      return (
        <svg {...base}>
          <path d="m8 4 12 12" />
          <path d="m14 4 6 6" />
          <path d="M4 14 14 4" />
          <path d="m4 14 6 6" />
          <path d="m6 18-3 3" />
        </svg>
      );

    case 'dental':
      return (
        <svg {...base}>
          <path d="M8 4c-3 0-4 3-3 6 1 4 2 10 4 10 1.5 0 1-5 3-5s1.5 5 3 5c2 0 3-6 4-10 1-3 0-6-3-6-2 0-2.5 1-4 1S10 4 8 4Z" />
        </svg>
      );

    case 'chevronLeft':
      return <svg {...base}><path d="m15 18-6-6 6-6" /></svg>;

    case 'chevronRight':
      return <svg {...base}><path d="m9 18 6-6-6-6" /></svg>;

    default:
      return null;
  }
};


const EventTypeIcon = ({ type, size = 20 }) => {
  if (type === 'lesson') {
    return <HorseHeadIcon width={size} height={size} aria-hidden="true" />;
  }

  if (type === 'other') {
    return <SvgIcon name="calendar" size={size} />;
  }

  return <SvgIcon name={type} size={size} />;
};


const fmt12 = value =>
  new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });


const fmtFullDate = value =>
  value.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });


const fmtShortDate = value =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });


const durationMinutes = event => {
  if (
    event.all_day ||
    !event.start_datetime ||
    !event.end_datetime
  ) {
    return null;
  }

  const minutes =
    Math.round(
      (
        new Date(event.end_datetime) -
        new Date(event.start_datetime)
      ) / 60000
    );

  return minutes > 0 ? minutes : null;
};


const CalendarPage = () => {
  const navigate = useNavigate();
  const now = new Date();

  const [year, setYear] =
    useState(now.getFullYear());

  const [month, setMonth] =
    useState(now.getMonth());

  const [events, setEvents] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [selectedDay, setSelectedDay] =
    useState(now.getDate());

  const [view, setView] =
    useState('month');

  const [upcomingEvents, setUpcomingEvents] =
    useState([]);

  const [eventFilter, setEventFilter] =
    useState('all');


  useEffect(() => {
    setLoading(true);

    api
      .get(`/calendar?month=${month + 1}&year=${year}`)
      .then(response => setEvents(response.data || []))
      .catch(() => toast.error('Failed to load events'))
      .finally(() => setLoading(false));
  }, [month, year]);


  useEffect(() => {
    api
      .get('/calendar/upcoming')
      .then(response => setUpcomingEvents(response.data || []))
      .catch(() => {});
  }, []);


  useEffect(() => {
    const lastDay =
      new Date(year, month + 1, 0).getDate();

    setSelectedDay(day =>
      Math.min(day, lastDay)
    );
  }, [month, year]);


  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(value => value - 1);
    } else {
      setMonth(value => value - 1);
    }
  };


  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(value => value + 1);
    } else {
      setMonth(value => value + 1);
    }
  };


  const firstDay =
    new Date(year, month, 1).getDay();

  const daysInMonth =
    new Date(year, month + 1, 0).getDate();

  const daysInPrev =
    new Date(year, month, 0).getDate();

  const cells = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({
      day: daysInPrev - i,
      current: false
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({
      day,
      current: true
    });
  }

  while (cells.length % 7 !== 0) {
    cells.push({
      day:
        cells.length -
        daysInMonth -
        firstDay +
        1,
      current: false
    });
  }


  const filteredEvents = useMemo(
    () =>
      eventFilter === 'all'
        ? events
        : events.filter(
            event =>
              event.event_type === eventFilter
          ),
    [events, eventFilter]
  );


  const filteredUpcoming = useMemo(
    () =>
      [...upcomingEvents]
        .filter(
          event =>
            eventFilter === 'all' ||
            event.event_type === eventFilter
        )
        .sort(
          (a, b) =>
            new Date(a.start_datetime) -
            new Date(b.start_datetime)
        ),
    [upcomingEvents, eventFilter]
  );


  const eventsOnDay = day => {
    if (!day) return [];

    const target =
      new Date(year, month, day);

    target.setHours(0, 0, 0, 0);

    return filteredEvents.filter(event => {
      const start =
        new Date(event.start_datetime);

      start.setHours(0, 0, 0, 0);

      const end =
        event.end_datetime
          ? new Date(event.end_datetime)
          : new Date(start);

      end.setHours(23, 59, 59, 0);

      return target >= start && target <= end;
    });
  };


  const selectedEvents =
    eventsOnDay(selectedDay).sort(
      (a, b) =>
        new Date(a.start_datetime) -
        new Date(b.start_datetime)
    );


  const selectedDate =
    new Date(year, month, selectedDay);


  const isToday = day =>
    day === now.getDate() &&
    month === now.getMonth() &&
    year === now.getFullYear();


  const handleDelete = async id => {
    if (!window.confirm('Delete this event?')) return;

    try {
      await api.delete(`/calendar/${id}`);

      setEvents(prev =>
        prev.filter(event => event.id !== id)
      );

      setUpcomingEvents(prev =>
        prev.filter(event => event.id !== id)
      );

      toast.success('Event deleted');

    } catch {
      toast.error('Failed to delete');
    }
  };


  const renderEventCard = (
    event,
    { showDate = false } = {}
  ) => {
    const type =
      event.event_type || 'other';

    const color =
      EVENT_COLORS[type] ||
      EVENT_COLORS.other;

    const tint =
      EVENT_TINTS[type] ||
      EVENT_TINTS.other;

    const horseName =
      event.horse?.name ||
      event.horse_name ||
      '';

    const typeName =
      EVENT_LABELS[type] || 'Event';

    const minutes =
      durationMinutes(event);

    return (
      <div
        key={event.id}
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: '76px 42px minmax(0,1fr) auto',
          gap: 10,
          alignItems: 'center',
          background: tint,
          border: `1px solid ${color}24`,
          borderRadius: 12,
          padding: '11px 12px 11px 15px',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 4,
            background: color
          }}
        />

        <div>
          <div
            style={{
              color,
              fontSize: 12,
              fontWeight: 800
            }}
          >
            {event.all_day
              ? 'All day'
              : fmt12(event.start_datetime)}
          </div>

          <div
            style={{
              color: 'var(--text-muted)',
              fontSize: 9,
              marginTop: 3
            }}
          >
            {showDate
              ? fmtShortDate(event.start_datetime)
              : minutes
                ? `${minutes} min`
                : ''}
          </div>
        </div>

        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            border: `1px solid ${color}24`,
            background: '#fff',
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <EventTypeIcon type={type} size={20} />
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 750
            }}
          >
            {typeName}
            {horseName ? ` · ${horseName}` : ''}
          </div>

          {event.provider && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-secondary)',
                marginTop: 2
              }}
            >
              {event.provider}
            </div>
          )}

          {event.title && (
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-secondary)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {event.title}
            </div>
          )}

          {event.notes && (
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-muted)',
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {event.notes}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 5
          }}
        >
          <button
            onClick={() =>
              navigate(`/calendar/add?edit=${event.id}`)
            }
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: 18,
              cursor: 'pointer',
              padding: '0 5px'
            }}
            aria-label="Edit event"
          >
            •••
          </button>

          {event.event_type === 'show' && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => navigate('/shows')}
            >
              Prep
            </button>
          )}
        </div>
      </div>
    );
  };


  return (
    <div
      style={{
        display: 'grid',
        gap: 14
      }}
    >
      {/* ======================================================
          MAIN CALENDAR CARD
          ====================================================== */}

      <div
        className="card"
        style={{
          padding: 0,
          overflow: 'hidden',
          borderRadius: 17,
          border: '1px solid #eadfd5'
        }}
      >
        <div
          style={{
            padding: '14px 14px 12px',
            background: '#fff'
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '42px 1fr 42px',
              alignItems: 'center'
            }}
          >
            <button
              onClick={prevMonth}
              aria-label="Previous month"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: '1px solid var(--border-color)',
                background: '#fff',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SvgIcon
                name="chevronLeft"
                size={19}
              />
            </button>

            <div
              style={{
                textAlign: 'center',
                fontFamily: 'Georgia, serif',
                fontSize: 20,
                fontWeight: 700
              }}
            >
              {MONTHS[month]} {year}
            </div>

            <button
              onClick={nextMonth}
              aria-label="Next month"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: '1px solid var(--border-color)',
                background: '#fff',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SvgIcon
                name="chevronRight"
                size={19}
              />
            </button>
          </div>


          <div
            style={{
              width: 220,
              maxWidth: '100%',
              margin: '10px auto 0',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              border: '1px solid var(--border-color)',
              borderRadius: 10,
              overflow: 'hidden'
            }}
          >
            <button
              onClick={() => setView('month')}
              style={{
                height: 34,
                border: 'none',
                background:
                  view === 'month'
                    ? 'var(--cr-primary)'
                    : '#fff',
                color:
                  view === 'month'
                    ? '#fff'
                    : 'var(--text-primary)',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Month
            </button>

            <button
              onClick={() => setView('upcoming')}
              style={{
                height: 34,
                border: 'none',
                borderLeft:
                  '1px solid var(--border-color)',
                background:
                  view === 'upcoming'
                    ? 'var(--cr-primary)'
                    : '#fff',
                color:
                  view === 'upcoming'
                    ? '#fff'
                    : 'var(--text-primary)',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Agenda
            </button>
          </div>
        </div>


        {view === 'month' && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7,1fr)',
                background: '#fff',
                borderTop:
                  '1px solid #eee6de'
              }}
            >
              {DAYS.map(day => (
                <div
                  key={day}
                  style={{
                    textAlign: 'center',
                    padding: '9px 0 7px',
                    fontSize: 10,
                    fontWeight: 750,
                    color: 'var(--text-secondary)'
                  }}
                >
                  {day}
                </div>
              ))}
            </div>


            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7,1fr)',
                background: '#fff',
                padding: '0 10px 10px'
              }}
            >
              {cells.map((cell, index) => {
                const dayEvents =
                  cell.current
                    ? eventsOnDay(cell.day)
                    : [];

                const selected =
                  cell.current &&
                  cell.day === selectedDay;

                const today =
                  cell.current &&
                  isToday(cell.day);

                return (
                  <button
                    key={index}
                    onClick={() =>
                      cell.current &&
                      setSelectedDay(cell.day)
                    }
                    disabled={!cell.current}
                    style={{
                      minHeight: 56,
                      border: '1px solid #eee6de',
                      borderRadius: 10,
                      background: selected
                        ? '#fff9fb'
                        : '#fff',
                      cursor: cell.current
                        ? 'pointer'
                        : 'default',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 5,
                      padding: '7px 2px',
                      margin: 1,
                      opacity: cell.current ? 1 : .38
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight:
                          selected || today
                            ? 750
                            : 500,
                        background: selected
                          ? 'var(--cr-primary)'
                          : 'transparent',
                        border:
                          today && !selected
                            ? '1px solid var(--cr-primary)'
                            : '1px solid transparent',
                        color: selected
                          ? '#fff'
                          : cell.current
                            ? 'var(--text-primary)'
                            : 'var(--text-muted)'
                      }}
                    >
                      {cell.day}
                    </span>

                    <span
                      style={{
                        display: 'flex',
                        gap: 3,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        minHeight: 6
                      }}
                    >
                      {dayEvents
                        .slice(0, 3)
                        .map((event, dotIndex) => (
                          <span
                            key={dotIndex}
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background:
                                EVENT_COLORS[
                                  event.event_type
                                ] ||
                                EVENT_COLORS.other
                            }}
                          />
                        ))}
                    </span>
                  </button>
                );
              })}
            </div>


            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                overflowX: 'auto',
                padding: '9px 12px 11px',
                borderTop:
                  '1px solid #eee6de',
                scrollbarWidth: 'none'
              }}
            >
              <select
                value={eventFilter}
                onChange={event =>
                  setEventFilter(event.target.value)
                }
                style={{
                  height: 34,
                  borderRadius: 999,
                  border:
                    '1px solid var(--border-color)',
                  background: '#fff',
                  padding: '0 10px',
                  fontSize: 10,
                  fontWeight: 650,
                  flexShrink: 0
                }}
              >
                <option value="all">
                  All events
                </option>

                {Object.entries(
                  EVENT_LABELS
                ).map(([value, label]) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {label}
                  </option>
                ))}
              </select>

              {[
                'lesson',
                'show',
                'farrier',
                'vet',
                'bodywork',
                'worming'
              ].map(type => (
                <div
                  key={type}
                  style={{
                    height: 30,
                    padding: '0 9px',
                    borderRadius: 999,
                    border:
                      '1px solid #eee6de',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap',
                    fontSize: 9,
                    color:
                      'var(--text-secondary)',
                    flexShrink: 0
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background:
                        EVENT_COLORS[type]
                    }}
                  />

                  {EVENT_LABELS[type]}
                </div>
              ))}
            </div>
          </>
        )}


        {view === 'upcoming' && (
          <div
            style={{
              padding: 12,
              borderTop:
                '1px solid #eee6de',
              display: 'grid',
              gap: 8
            }}
          >
            {loading && (
              <div
                style={{
                  padding: 30,
                  textAlign: 'center'
                }}
              >
                <div className="spinner" />
              </div>
            )}

            {!loading &&
              filteredUpcoming.length === 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '30px 12px',
                  color:
                    'var(--text-muted)',
                  fontSize: 12
                }}
              >
                No upcoming events
              </div>
            )}

            {!loading &&
              filteredUpcoming.map(event =>
                renderEventCard(
                  event,
                  { showDate: true }
                )
              )}
          </div>
        )}
      </div>


      {/* ======================================================
          SELECTED DAY
          ====================================================== */}

      {view === 'month' && (
        <div
          className="card"
          style={{
            padding: 14,
            borderRadius: 17,
            border: '1px solid #eadfd5'
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 12
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: '#f5edf1',
                color: 'var(--cr-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <SvgIcon name="calendar" size={19} />
            </div>

            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'Georgia, serif',
                fontSize: 17,
                fontWeight: 700
              }}
            >
              {fmtFullDate(selectedDate)}
            </div>

            <button
              onClick={() =>
                navigate('/calendar/add')
              }
              style={{
                border: 'none',
                borderRadius: 999,
                height: 36,
                background:
                  'var(--cr-primary)',
                color: '#fff',
                padding: '0 14px',
                fontSize: 11,
                fontWeight: 750,
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              + Add event
            </button>
          </div>


          {loading ? (
            <div
              style={{
                padding: 28,
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <div className="spinner" />
            </div>
          ) : selectedEvents.length === 0 ? (
            <div
              style={{
                border:
                  '1px dashed #e1d8cf',
                borderRadius: 13,
                padding: '24px 12px',
                textAlign: 'center',
                color:
                  'var(--text-secondary)'
              }}
            >
              <div
                style={{
                  color: 'var(--text-muted)',
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 8
                }}
              >
                <SvgIcon
                  name="calendar"
                  size={25}
                />
              </div>

              <div
                style={{
                  fontSize: 12,
                  fontWeight: 650
                }}
              >
                Nothing scheduled for{' '}
                {MONTHS[month]} {selectedDay}
              </div>

              <button
                onClick={() =>
                  navigate('/calendar/add')
                }
                style={{
                  border: 'none',
                  background: 'transparent',
                  color:
                    'var(--cr-primary)',
                  fontSize: 11,
                  fontWeight: 750,
                  marginTop: 8,
                  cursor: 'pointer'
                }}
              >
                + Add event
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gap: 8
              }}
            >
              {selectedEvents.map(event =>
                renderEventCard(event)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default CalendarPage;
